import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Pencil } from 'lucide-react';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { TEAM_TOTALS_VISIBILITY } from 'utils/meetPoints';
import styles from './PointsVisibility.module.css';

// Who sees the Points page, for a school's Admins (points slice 4b, docs/school-carnivals-points-slice-4b.md
// in the portal repo, section 9): a badge beside the page's title, as the Results page shows "Preliminary"
// (Doug, 2026-10-26: not in the top row or on a line of its own, too cluttered; not on the House Totals card,
// which a school with no houses lacks). The badge shows the saved choice, grey Admins only (every meet's
// start) or green Admins and members, and looks clickable (a pencil, a darker shade on hover). It opens one
// popup with the two choices; the line saying what releasing shows appears under Admins and members while
// it is ticked. Save sends nothing when the choice is unchanged.
const PointsVisibility = ({ visibility = TEAM_TOTALS_VISIBILITY.ADMINS, onChange, saving = false, teamWords }) => {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(visibility);
  const released = visibility === TEAM_TOTALS_VISIBILITY.MEMBERS;

  const openPopup = () => {
    setChoice(visibility);
    setOpen(true);
  };

  const handleSave = () => {
    setOpen(false);
    if (choice !== visibility) onChange(choice);
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.badge} ${released ? styles.released : styles.adminsOnly}`}
        onClick={openPopup}
        disabled={saving}
        title="Change who sees this page"
      >
        {released ? 'Admins and members' : 'Admins only'}
        <Pencil size={14} className={styles.pencil} aria-hidden="true" />
      </button>
      <CustomNewConfirm
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Who sees this page?"
        confirmButtonText="Save"
        onConfirm={handleSave}
      >
        <div className={styles.choices}>
          <label className={styles.choice}>
            <input
              type="radio"
              name="pointsVisibility"
              value={TEAM_TOTALS_VISIBILITY.ADMINS}
              checked={choice === TEAM_TOTALS_VISIBILITY.ADMINS}
              onChange={() => setChoice(TEAM_TOTALS_VISIBILITY.ADMINS)}
            />
            Admins only
          </label>
          <label className={styles.choice}>
            <input
              type="radio"
              name="pointsVisibility"
              value={TEAM_TOTALS_VISIBILITY.MEMBERS}
              checked={choice === TEAM_TOTALS_VISIBILITY.MEMBERS}
              onChange={() => setChoice(TEAM_TOTALS_VISIBILITY.MEMBERS)}
            />
            Admins and members
          </label>
          {choice === TEAM_TOTALS_VISIBILITY.MEMBERS && (
            <p className={styles.consequence}>
              Members will see this page, with each {teamWords.lower.team}&apos;s points and each athlete&apos;s points.
            </p>
          )}
        </div>
      </CustomNewConfirm>
    </>
  );
};

PointsVisibility.propTypes = {
  // The saved setting (MeetPointsDashboardDto.teamTotalsVisibility).
  visibility: PropTypes.oneOf(Object.values(TEAM_TOTALS_VISIBILITY)),
  // Saves a new choice; the badge is disabled while it is saved.
  onChange: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  // The school's words for a house (utils/teamLabel.js).
  teamWords: PropTypes.shape({ lower: PropTypes.shape({ team: PropTypes.string }) }).isRequired,
};

export default PointsVisibility;
